/**
 * The poll schedule for the Socure IDV step, as pure functions over a clock.
 *
 * Extracted from `socure-idv.tsx` so the schedule can be tested at all. The step
 * is a React component in a package with no jsdom and no testing-library, so
 * anything left inside it is only verifiable by hand — and the cost of getting
 * this wrong is measured in requests per day against our own API. Nothing here
 * touches the DOM, React or timers; the caller owns `setTimeout` and passes the
 * clock in.
 *
 * WHAT CHANGED, and why the shape looks different from an ordinary poll loop:
 * the server now supports a long poll (`?wait=`), so a request no longer answers
 * "here is the state" but "here is the state, and I waited for it to change".
 * The backoff therefore governs the GAP BETWEEN HOLDS rather than a poll
 * interval.
 *
 * That distinction is the whole fix for the QR hand-off. The applicant scans on
 * the desktop and finishes on their PHONE while the desktop window stays open
 * and focused the entire time — so `visibilitychange` never fires, `focus` never
 * fires, and there is no client-side event to react to. Any interval-based
 * schedule leaves the desktop waiting out its own backoff after we already know:
 * measured against real production captures, up to ~30s on top of the ~12s it
 * took us to learn it. A held request removes that term entirely.
 */

export interface PollBackoffOptions {
  /** How long to ask the server to hold, in seconds. Must stay under its cap. */
  holdSeconds: number;
  /** The shortest gap between holds. */
  floorMs: number;
  /** The longest gap, once nothing has happened for a while. */
  ceilingMs: number;
  /** Multiplier applied to the gap after a hold that returned unchanged. */
  growth: number;
  /**
   * Consecutive unchanged holds before the gap starts growing.
   *
   * A capture takes minutes, so the first several empty holds are entirely
   * expected and must not be treated as abandonment. Only after this many does
   * the schedule start conceding that nobody is coming back.
   */
  patientHolds: number;
  /**
   * Below this fraction of the requested hold, the request did not hold at all.
   *
   * THE ENTIRE SAFETY CONDITION FOR `floorMs` IS THAT A REQUEST COSTS ~20s. If
   * something answers immediately instead, reopening at the floor is a ~2/s busy
   * loop per tab. That is not hypothetical: `wait` is a query param, so an old
   * server that ignores it, a proxy that strips it, or any fast error all
   * produce it — and a ROLLBACK produces the worst version, every open tab
   * flipping to a fast server at once. The `wait`-is-optional design covers an
   * old bundle against a new server; this covers a new bundle against an old
   * one, which is what a rollback actually is.
   */
  degradedReturnRatio: number;
  /** The gap to use once holds stop actually holding. */
  degradedFloorMs: number;
  /**
   * Random spread on a degraded gap, so every tab in a fleet does not retry on
   * the same tick after a deploy.
   */
  degradedJitterMs: number;
  /**
   * Two events for one return get one poll.
   *
   * Browsers do not agree on which event fires when a tab regains attention —
   * some fire `focus`, some `visibilitychange`, some both — and we listen for
   * both precisely so no browser is missed. Without this, "both" means every
   * return costs two requests.
   */
  attentionDedupeMs: number;
}

export const POLL_BACKOFF: PollBackoffOptions = {
  /**
   * Exactly the server's `MAX_HOLD_SECONDS`, so the server clamps rather than
   * rejects and the ceiling stays defined in one place. The server budgets its
   * whole response separately (`RESPONSE_BUDGET_MS`) to stay inside Heroku's 30s
   * router; this number is only the sleeping part and is not that bound.
   */
  holdSeconds: 20,
  /**
   * Effectively "immediately", which is only safe because a hold that returned
   * unchanged has already spent ~20s doing it — one request per ~20s, at far
   * better responsiveness than the 5s-and-backing-off interval this replaces.
   *
   * Note it is NOT less aggregate traffic than what it replaces: over ~7 minutes
   * the old schedule made ~16 requests and this makes ~18 holds. The win is
   * latency, not volume, and `degradedReturnRatio` is what keeps the volume
   * claim true when a request stops costing 20s.
   */
  floorMs: 500,
  /**
   * The idle ceiling, for a tab nobody is coming back to.
   *
   * A fixed fast poll never stopped: a desktop tab left open on the QR screen
   * was ~17k authenticated requests a day, each doing session + application +
   * scoping reads. Deliberately no hard stop — capping the number of attempts
   * would strand whoever finishes on their phone one minute after the cap, which
   * is the failure this step already had once.
   */
  ceilingMs: 30_000,
  growth: 1.5,
  patientHolds: 9,
  degradedReturnRatio: 0.5,
  /**
   * The interval the original incident ran at, which is the point: if holds are
   * not working we should be no worse than the behaviour this replaced, not 10x
   * more aggressive than it.
   */
  degradedFloorMs: 5_000,
  degradedJitterMs: 2_000,
  attentionDedupeMs: 1_000,
};

export interface PollBackoff {
  /** How long to wait before opening the next hold. */
  delayMs: number;
  /** Consecutive holds that returned with nothing changed. */
  emptyHolds: number;
}

export const startPolling = (
  options: PollBackoffOptions = POLL_BACKOFF
): PollBackoff => ({
  delayMs: options.floorMs,
  emptyHolds: 0,
});

/**
 * After a hold that returned unchanged.
 *
 * Stays at the floor while the capture is plausibly still happening — roughly
 * `patientHolds * holdSeconds` of real time, which covers a slow capture — then
 * decays geometrically toward the idle ceiling. A parked tab therefore ends up
 * costing the same as before; an applicant mid-capture never waits.
 *
 * `elapsedMs` is how long the request actually took, and it is what separates
 * "the server held for 20s and nothing happened" from "the server answered
 * instantly". The two look identical from the response body and want opposite
 * responses: reopen immediately, or back off hard.
 */
export const afterEmptyHold = (
  state: PollBackoff,
  elapsedMs: number,
  options: PollBackoffOptions = POLL_BACKOFF,
  /** Injected so the jitter is assertable. */
  random: () => number = Math.random
): PollBackoff => {
  const emptyHolds = state.emptyHolds + 1;

  // Nothing on the other end is holding, so raise the FLOOR — see
  // `degradedReturnRatio`.
  //
  // A floor rather than a fixed delay, which matters more than it looks. Holding
  // is gated per institution (`settings.socure.idvHoldSeconds`), so "the server
  // answers immediately" is not only a rollback symptom — it is the ordinary
  // steady state for any flow that has not opted in. Pinning the gap at the
  // degraded floor would leave an abandoned tab on such a flow polling every
  // ~6s forever, which is ~14k requests/day and very nearly the incident this
  // schedule exists to prevent. Decay still applies on top, so "off" degrades to
  // an ordinary backing-off poll that ends at the same 30s ceiling.
  const requestedMs = options.holdSeconds * 1_000;
  const degraded = elapsedMs < requestedMs * options.degradedReturnRatio;
  const floorMs = degraded
    ? options.degradedFloorMs + Math.floor(random() * options.degradedJitterMs)
    : options.floorMs;

  if (emptyHolds <= options.patientHolds) {
    return { delayMs: floorMs, emptyHolds };
  }
  return {
    delayMs: Math.min(
      options.ceilingMs,
      Math.max(floorMs, state.delayMs * options.growth)
    ),
    emptyHolds,
  };
};

/**
 * The applicant came back: reset to the floor and reopen a hold now.
 *
 * Still worth listening for even though it cannot help the QR case. It is the
 * SAME-DEVICE path this rescues — below the `lg` breakpoint the capture opens in
 * a new tab, so returning to this one is a genuine attention event — plus the
 * case where someone alt-tabs away from the QR screen and back. Neither is the
 * primary scenario, and neither is a reason to keep an interval.
 */
export const onAttention = (
  state: PollBackoff,
  now: number,
  lastAttentionAtMs: number | null,
  options: PollBackoffOptions = POLL_BACKOFF
): { state: PollBackoff; attentionAtMs: number; pollNow: boolean } => {
  if (
    lastAttentionAtMs !== null &&
    now - lastAttentionAtMs < options.attentionDedupeMs
  ) {
    return { state, attentionAtMs: lastAttentionAtMs, pollNow: false };
  }
  return {
    state: { delayMs: options.floorMs, emptyHolds: 0 },
    attentionAtMs: now,
    pollNow: true,
  };
};
